import { getToken, filterRequestForProvisioningItem } from 'utils';
import { ROUTE } from 'constant';
import { getKey, removePendingActions, getErrorMessages } from './utils';
import { getConfig } from './config';

const getUrl = ({ url = '', baseUrl = null }) => {
  const {
    apiKeyManagerUrl = '',
    apiBillingUrl = '',
    apiResellerUrl = '',
    apiBaseUrl = '',
    apiTelemetryUrl = '',
    apiMonitoringUrl = '',
    apiAutoScalingUrl = '',
    apiCertificateManagerUrl,
    apiDBaaSUrl = '',
    apiAdServerUrl = '',
    apiContainerRegistryUrl = '',
    apiEnvRootUrl = '',
  } = getConfig() || {};

  const baseUrlMapper = {
    keymanager: `${apiKeyManagerUrl}/ssh/keys`,
    billing: apiBillingUrl,
    telemetry: apiTelemetryUrl,
    monitoring: apiMonitoringUrl,
    reseller: `${apiResellerUrl}/`,
    autoscaling: apiAutoScalingUrl,
    certificateManager: apiCertificateManagerUrl,
    database: apiDBaaSUrl,
    adServer: apiAdServerUrl,
    containerRegistry: apiContainerRegistryUrl,
  };

  let base = `${apiBaseUrl}/`;

  if (baseUrl) {
    base = baseUrlMapper[baseUrl] || baseUrl;
  }

  if (!base.startsWith('http') && apiEnvRootUrl) {
    base = `${apiEnvRootUrl}${base}`;
  }

  return base + url;
};

const getHeaders = ({
  headers, responseType, baseUrl, addContract = true,
}) => {
  const jwtToken = getToken('jwt');
  const token = getToken();

  const headersObj = {
    ...headers,
  };

  if (responseType === 'json') {
    headersObj['Content-Type'] = 'application/json; charset=utf-8';
  }

  if (typeof headersObj.Authorization === 'undefined') {
    if (jwtToken && baseUrl !== 'billing') {
      headersObj.Authorization = `Bearer ${jwtToken}`;
    } else if (token) {
      headersObj.Authorization = `Basic ${token}`;
    }
  }

  if (getKey('isReseller') && addContract && getKey('selectedContractId')) {
    headersObj['X-Contract-Number'] = getKey('selectedContractId');
  }
  // TODO: firefox issue
  headersObj.credentials = 'include'; // include, *same-origin, omit
  headersObj['Referrer-Policy'] = 'no-referrer'; // include, *same-origin, omit
  headersObj.mode = 'no-cors'; // include, *same-origin, omit

  return new Headers(headersObj);
};

const request = ({
  url,
  headers,
  baseUrl,
  secure,
  body = null,
  action,
  method,
  responseType = 'json',
  addContract,
}) => (new Promise((resolve) => {
  const requestOptions = {
    headers: getHeaders({
      headers, responseType, baseUrl, addContract,
    }),
    method,
  };

  if (body) {
    requestOptions.body = JSON.stringify(body);
  }

  fetch(getUrl({ url, baseUrl }), requestOptions)
    .then(async (response) => {
      const { status } = response;

      filterRequestForProvisioningItem({ action, response, baseUrl });

      if (method === 'HEAD') {
        return resolve({ httpStatus: status });
      }

      if (method === 'DELETE' && (status >= 200 && status < 300)) {
        return resolve({ success: true });
      }

      if (status === 202) {
        return resolve({ success: true });
      }

      if (status === 404 || (status >= 500 && status < 600)) {
        const responseBody = await response.json();
        const { errors } = responseBody || {};
        const errorMessages = getErrorMessages(errors);

        removePendingActions();

        if (status === 404) {
          return {
            httpStatus: 404,
            messages: errorMessages || [{ message: 'API service not found!' }],
          };
        }

        return {
          httpStatus: 500,
          messages: errorMessages || [{ message: 'API service not available!' }],
        };
      }

      if (responseType !== 'json') {
        return resolve(response.text());
      }

      return response.json();
    })
    .then((e) => {
      if (!e) {
        removePendingActions();
        return {};
      }

      const { httpStatus = '' } = e;

      if (httpStatus === 401 && secure) {
        let _url = `${process.env.PUBLIC_URL}${ROUTE.NON_SECURE.LOGIN.path}?return-url=${window.location.href.replace(process.env.PUBLIC_URL, '')}`;
        if (getKey('isAccessByDCD')) _url = `${process.env.PUBLIC_URL}${ROUTE.NON_SECURE.DCD_LOGIN_ERROR.path}`;
        window.location.href = _url;
        removePendingActions();
      }

      return resolve(e);
    }).catch(() => {
      removePendingActions();
      resolve({ httpStatus: 404, messages: [{ message: 'API service not found!' }] });
    });
}));

export const rest = {
  get: ({
    url, headers, baseUrl, secure = true, action = 'GET', addContract,
  }) => request({
    url, headers, baseUrl, secure, action, method: 'GET', addContract,
  }),

  post: ({
    url, headers, body, baseUrl, action = 'CREATE',
  }) => request({
    url, headers, body, baseUrl, action, method: 'POST',
  }),

  put: ({
    url, headers, body, baseUrl, action = 'UPDATE',
  }) => request({
    url, headers, body, baseUrl, action, method: 'PUT',
  }),

  delete: ({
    url, headers, baseUrl, action = 'DELETE',
  }) => request({
    url, headers, baseUrl, action, method: 'DELETE',
  }),

  patch: ({
    url, headers, body, baseUrl, action = 'UPDATE',
  }) => request({
    url, headers, body, baseUrl, action, method: 'PATCH',
  }),

  head: ({
    url, headers, baseUrl, action = 'HEAD',
  }) => request({
    url, headers, baseUrl, action, method: 'HEAD',
  }),

  request: ({
    url, headers, baseUrl, secure = true, body = null, method = 'GET', responseType = 'json',
  }) => request({
    url, headers, baseUrl, secure, action: method, method, responseType, body,
  }),
};
